import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const EMPTY_ROUTE = {
  dayNumber: "",
  name: "",
  description: "",
  distanceKm: "",
  color: "#e55555",
  gpxData: "",
  overnightStop: { name: "", description: "" }
};

function RouteEditor({ route, csrfToken, onSaved, onDeleted, onCancel }) {
  const [form, setForm] = useState({
    dayNumber: route.dayNumber ?? "",
    name: route.name || "",
    description: route.description || "",
    distanceKm: route.distanceKm || "",
    color: route.color || "#e55555",
    gpxData: route.gpxData || "",
    overnightStop: route.overnightStop || { name: "", description: "" }
  });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [coordCount, setCoordCount] = useState(route.coordinates?.length || 0);

  const isNew = !route._id;
  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));
  const setStop = (field, value) => setForm(f => ({
    ...f, overnightStop: { ...f.overnightStop, [field]: value }
  }));

  const save = async () => {
    if (!form.name.trim()) { setError("Naam is verplicht"); return; }
    if (form.dayNumber === "" || isNaN(Number(form.dayNumber))) { setError("Etappenummer is verplicht"); return; }
    setSaving(true);
    setError("");
    const body = JSON.stringify({
      ...form,
      dayNumber: Number(form.dayNumber),
      distanceKm: form.distanceKm !== "" ? Number(form.distanceKm) : 0
    });
    const res = await fetch(
      isNew ? `${API}/admin/event-routes` : `${API}/admin/event-routes/${route._id}`,
      {
        method: isNew ? "POST" : "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", "CSRF-Token": csrfToken },
        body
      }
    );
    setSaving(false);
    if (!res.ok) {
      const data = await res.json();
      setError(data.error || "Opslaan mislukt");
      return;
    }
    const saved = await res.json();
    setCoordCount(saved.coordinates?.length || 0);
    onSaved(saved, isNew);
  };

  const remove = async () => {
    if (!window.confirm(`Etappe "${form.name}" verwijderen?`)) return;
    await fetch(`${API}/admin/event-routes/${route._id}`, {
      method: "DELETE",
      credentials: "include",
      headers: { "CSRF-Token": csrfToken }
    });
    onDeleted(route._id);
  };

  return (
    <div className="content-editor">
      <div className="content-editor-meta">
        <div className="content-editor-field">
          <label>Etappenummer</label>
          <input
            type="number"
            value={form.dayNumber}
            onChange={e => set("dayNumber", e.target.value)}
            placeholder="1"
          />
        </div>
        <div className="content-editor-field">
          <label>Naam</label>
          <input
            value={form.name}
            onChange={e => set("name", e.target.value)}
            placeholder="bijv. Den Helder → Harlingen"
          />
        </div>
        <div className="content-editor-field">
          <label>Afstand (km)</label>
          <input
            type="number"
            value={form.distanceKm}
            onChange={e => set("distanceKm", e.target.value)}
            placeholder="145"
          />
        </div>
        <div className="content-editor-field">
          <label>Kleur op de kaart</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="color"
              value={form.color}
              onChange={e => set("color", e.target.value)}
              style={{ width: 40, height: 32, padding: 2, cursor: "pointer" }}
            />
            <input
              value={form.color}
              onChange={e => set("color", e.target.value)}
              placeholder="#e55555"
              style={{ flex: 1 }}
            />
          </div>
        </div>
        <div className="content-editor-field">
          <label>Tussenstop naam</label>
          <input
            value={form.overnightStop.name}
            onChange={e => setStop("name", e.target.value)}
            placeholder="bijv. Camping De Waddenzee"
          />
        </div>
        <div className="content-editor-field">
          <label>Tussenstop info</label>
          <input
            value={form.overnightStop.description}
            onChange={e => setStop("description", e.target.value)}
            placeholder="bijv. Kleine natuurcamping, reserveren aanbevolen"
          />
        </div>
      </div>

      <label>Omschrijving</label>
      <textarea
        className="content-main-body"
        style={{ minHeight: 60 }}
        value={form.description}
        onChange={e => set("description", e.target.value)}
        placeholder="Korte omschrijving van de etappe"
      />

      <label>
        GPX-data
        {coordCount > 0
          ? <small style={{ marginLeft: 8, color: "#2a9d2a" }}>✓ {coordCount} coördinaten opgeslagen — leeg laten om te behouden</small>
          : <small style={{ marginLeft: 8, color: "#888" }}>Nog geen GPX — plak volledige bestandsinhoud</small>}
      </label>
      <textarea
        className="content-main-body"
        style={{ minHeight: 120, fontFamily: "monospace", fontSize: 11 }}
        value={form.gpxData}
        onChange={e => set("gpxData", e.target.value)}
        placeholder={'<?xml version="1.0"?>\n<gpx ...>\n  <trk><trkseg>\n    <trkpt lat="52.9" lon="4.7"/>\n    ...\n  </trkseg></trk>\n</gpx>'}
      />

      {error && <p className="content-error">{error}</p>}

      <div className="content-editor-actions">
        <button onClick={save} disabled={saving}>{saving ? "Opslaan…" : "Opslaan"}</button>
        <button className="btn-secondary" onClick={onCancel}>Annuleren</button>
        {!isNew && <button className="btn-danger" onClick={remove}>Verwijderen</button>}
      </div>
    </div>
  );
}

export default function AdminRoutes({ csrfToken }) {
  const [routes, setRoutes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [reparsing, setReparsing] = useState(false);
  const [reparseResult, setReparseResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/event-routes`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(data => setRoutes(Array.isArray(data) ? data : []));
  }, []);

  const reparseAll = async () => {
    setReparsing(true);
    setReparseResult(null);
    const res = await fetch(`${API}/admin/event-routes/reparse-all`, {
      method: "POST",
      credentials: "include",
      headers: { "CSRF-Token": csrfToken }
    });
    const data = await res.json();
    setReparseResult(data);
    setReparsing(false);
    // Refresh list to show updated coord counts
    const list = await fetch(`${API}/event-routes`, { credentials: "include" }).then(r => r.json());
    if (Array.isArray(list)) setRoutes(list);
  };

  const handleSaved = (saved, isNew) => {
    if (isNew) {
      setRoutes(prev => [...prev, saved].sort((a, b) => a.dayNumber - b.dayNumber));
    } else {
      setRoutes(prev => prev.map(r => r._id === saved._id ? saved : r).sort((a, b) => a.dayNumber - b.dayNumber));
    }
    setEditing(null);
  };

  const handleDeleted = (id) => {
    setRoutes(prev => prev.filter(r => r._id !== id));
    setEditing(null);
  };

  if (editing !== null) {
    return (
      <RouteEditor
        route={editing}
        csrfToken={csrfToken}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div>
      <div className="content-list-header">
        <h2>Etappes</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={reparseAll} disabled={reparsing}>
            {reparsing ? "Bezig…" : "Herparse alle GPX"}
          </button>
          <button onClick={() => setEditing({ ...EMPTY_ROUTE })}>+ Nieuwe etappe</button>
        </div>
      </div>
      {reparseResult && (
        <div style={{ fontSize: "0.85rem", margin: "4px 0" }}>
          <span style={{ color: reparseResult.updated > 0 ? "#080" : "#c00" }}>
            {reparseResult.updated}/{reparseResult.total} routes succesvol geparst.
          </span>
          {reparseResult.errors?.length > 0 && (
            <ul style={{ color: "#c00", margin: "4px 0 0 16px", padding: 0 }}>
              {reparseResult.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
          {reparseResult.total === 0 && (
            <span style={{ color: "#c07000", marginLeft: 8 }}>
              — geen routes met opgeslagen GPX gevonden. Plak GPX opnieuw per etappe.
            </span>
          )}
        </div>
      )}
      <div className="content-list">
        {routes.length === 0 && <p>Nog geen etappes.</p>}
        {routes.map(r => (
          <div key={r._id} className="content-list-item">
            <span>
              <span
                style={{ display: "inline-block", width: 12, height: 12, borderRadius: 2, background: r.color, marginRight: 8, verticalAlign: "middle" }}
              />
              <strong>Dag {r.dayNumber}</strong> — {r.name}
              {r.distanceKm > 0 && <small> ({r.distanceKm} km)</small>}
              {r.coordinates?.length > 0 && <small style={{ color: "#080" }}> ✓ GPX</small>}
            </span>
            <button className="btn-secondary" onClick={() => setEditing(r)}>Bewerken</button>
          </div>
        ))}
      </div>
    </div>
  );
}
